/**
 * CopyableCode Component
 *
 * Displays a code/identifier with copy-to-clipboard functionality.
 * Optimized for table cells and inline display.
 */

import React, { useState } from 'react';

interface CopyableCodeProps {
  value: string;
  className?: string;
  showCopyButton?: boolean;
}

/**
 * CopyableCode component - displays code with copy functionality
 */
export const CopyableCode: React.FC<CopyableCodeProps> = ({
  value,
  className = '',
  showCopyButton = true,
}) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return (
    <div className={`inline-flex items-center gap-1 ${className}`}>
      <code className="text-sm font-mono bg-gray-100 px-2 py-0.5 rounded text-gray-700">
        {value}
      </code>
      {showCopyButton && (
        <button
          onClick={handleCopy}
          className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
          title={copied ? 'Copied!' : 'Copy to clipboard'}
          type="button"
        >
          {copied ? (
            <svg className="w-4 h-4 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          ) : (
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
          )}
        </button>
      )}
    </div>
  );
};
